import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import inspectionService from '../../services/inspectionService';
import './InspectionActions.css';
import Modal from '../Modal';
import Notification from '../Notification';
import { useAuthContext } from '../../context/AuthContext';
import { INSPECTION_STATUS, ROLES } from '../../utils/constants';
import logger from '../../utils/logger';

const { ADMINISTRATOR, INSPECTOR } = ROLES;

const InspectionActions = ({ inspectionId, onRejectInspection, inspectionStatus }) => {
    const navigate = useNavigate();
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [showRejectModal, setShowRejectModal] = useState(false);
    const [rejectionReason, setRejectionReason] = useState('');
    const [isDeleting, setIsDeleting] = useState(false);
    const [notification, setNotification] = useState({ visible: false, message: '', type: 'info' });
    const { currentUser } = useAuthContext();

    const showNotification = (message, type = 'info') => {
        setNotification({ visible: true, message, type });
        setTimeout(() => {
            setNotification({ visible: false, message: '', type: 'info' });
        }, 5000);
    };

    const handleBackToDashboard = () => {
        logger.info('Volviendo al inicio...', {
            inspectionId: inspectionId,
            currentUserEmail: currentUser?.email,
        }, 'InspectionActions');
        navigate('/');
    };

    const handleDeleteClick = () => {
        setShowDeleteModal(true);
    };

    const handleRejectClick = () => {
        setShowRejectModal(true);
        setRejectionReason('');
    };

    const handleCancelDelete = () => {
        setShowDeleteModal(false);
    };

    const handleCancelReject = () => {
        setShowRejectModal(false);
        setRejectionReason('');
    };

    const handleConfirmDelete = async () => {
        try {
            setIsDeleting(true);
            logger.info('Borrando inspección...', {
                inspectionId: inspectionId,
                currentUserEmail: currentUser?.email,
            }, 'InspectionActions');
            await inspectionService.deleteInspection(inspectionId);

            showNotification('Inspección borrada exitosamente', 'success');
            navigate('/');
        } catch (error) {
            logger.error('Error deleting inspection:', error, null, 'InspectionActions');
            showNotification(`Error al borrar la inspección: ${error.message}`, 'error');
        } finally {
            setIsDeleting(false);
            setShowDeleteModal(false);
        }
    };

    const handleConfirmReject = async () => {
        if (!rejectionReason.trim()) {
            showNotification('Por favor, ingresa una razón para el rechazo', 'error');
            return;
        }

        await onRejectInspection({ content: rejectionReason.trim() });
        setRejectionReason('');
        setShowRejectModal(false);
        showNotification('Inspección rechazada exitosamente', 'success');
    };

    const enableDeleteInspectionButton = () => {
        if (currentUser.role !== ADMINISTRATOR) {
            return false;
        }
        return true;
    };

    const enableRejectInspectionButton = () => {
        if ((currentUser.role === ADMINISTRATOR || currentUser.role === INSPECTOR) && ![INSPECTION_STATUS.APPROVED, INSPECTION_STATUS.REJECTED].includes(inspectionStatus)) {
            return true;
        }
        return false;
    };

    const getButtonCount = () => {
        let count = 1; // Siempre hay el botón "Volver"
        if (enableDeleteInspectionButton()) count++;
        if (enableRejectInspectionButton()) count++;
        return count;
    };

    return (
        <>
            <div className="inspection-actions">
                <div className={`actions-container ${getButtonCount() === 3 ? 'three-buttons' : getButtonCount() === 2 ? 'two-buttons' : 'one-button'}`}>
                    <button
                        onClick={handleBackToDashboard}
                        className="action-btn back-btn"
                    >
                        <svg className="btn-icon" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <path d="M19 12H5"></path>
                            <path d="M12 19l-7-7 7-7"></path>
                        </svg>
                        Volver al Inicio
                    </button>

                    {enableRejectInspectionButton() && (
                        <button
                            onClick={handleRejectClick}
                            className="action-btn reject-btn"
                        >
                            <svg className="btn-icon" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                <path d="M18 6L6 18M6 6l12 12"></path>
                            </svg>
                            Rechazar Inspección
                        </button>
                    )}

                    {enableDeleteInspectionButton() && (
                        <button
                            onClick={handleDeleteClick}
                            className="action-btn delete-btn"
                        >
                            <svg className="btn-icon" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                <path d="M3 6h18"></path>
                                <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6"></path>
                                <path d="M8 6V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path>
                            </svg>
                            Borrar Inspección
                        </button>
                    )}
                </div>
            </div>

            {/* Modal de confirmación para borrar */}
            <Modal
                isOpen={showDeleteModal}
                onClose={handleCancelDelete}
                title="Confirmar Borrado"
                size="medium"
                showCloseButton={true}
                closeOnBackdropClick={false}
            >
                <div className="delete-modal-content">
                    <div className="warning-icon">
                        <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"></path>
                            <line x1="12" y1="9" x2="12" y2="13"></line>
                            <line x1="12" y1="17" x2="12.01" y2="17"></line>
                        </svg>
                    </div>

                    <p className="warning-text">
                        ¿Estás seguro de que deseas borrar esta inspección?
                    </p>

                    <p className="warning-details">
                        Esta acción eliminará permanentemente:
                    </p>

                    <ul className="warning-list">
                        <li>La información de la inspección</li>
                        <li>Todas las fotografías asociadas</li>
                        <li>El historial de actividades</li>
                    </ul>

                    <p className="warning-final">
                        <strong>Esta acción no se puede deshacer.</strong>
                    </p>

                    <div className="modal-actions">
                        <button
                            onClick={handleCancelDelete}
                            className="modal-btn cancel-btn"
                            disabled={isDeleting}
                        >
                            Cancelar
                        </button>
                        <button
                            onClick={handleConfirmDelete}
                            className="modal-btn confirm-btn"
                            disabled={isDeleting}
                        >
                            {isDeleting ? (
                                <>
                                    <div className="loading-spinner-small"></div>
                                    Borrando...
                                </>
                            ) : (
                                'Sí, Borrar Inspección'
                            )}
                        </button>
                    </div>
                </div>
            </Modal>

            {/* Modal de confirmación para rechazar */}
            <Modal
                isOpen={showRejectModal}
                onClose={handleCancelReject}
                title="Rechazar Inspección"
                size="medium"
                showCloseButton={true}
                closeOnBackdropClick={false}
            >
                <div className="reject-modal-content">
                    <div className="warning-icon reject-icon">
                        <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <path d="M18 6L6 18M6 6l12 12"></path>
                        </svg>
                    </div>

                    <p className="warning-text">
                        ¿Estás seguro de que deseas rechazar esta inspección?
                    </p>

                    <p className="warning-details">
                        Por favor, proporciona una razón detallada para el rechazo:
                    </p>

                    <div className="rejection-reason-container">
                        <textarea
                            value={rejectionReason}
                            onChange={(e) => setRejectionReason(e.target.value)}
                            placeholder="Describe la razón del rechazo..."
                            className="rejection-reason-textarea"
                            rows="4"
                            maxLength="500"
                        />
                        <div className="character-count">
                            {rejectionReason.length}/500 caracteres
                        </div>
                    </div>

                    <p className="warning-final">
                        <strong>Esta acción cambiará el estado de la inspección a "Rechazada".</strong>
                    </p>

                    <div className="modal-actions">
                        <button
                            onClick={handleCancelReject}
                            className="modal-btn cancel-btn"
                        >
                            Cancelar
                        </button>
                        <button
                            onClick={handleConfirmReject}
                            className="modal-btn reject-confirm-btn"
                            disabled={!rejectionReason.trim()}
                        >
                            {!rejectionReason.trim() ? (
                                <>
                                    <div className="loading-spinner-small"></div>
                                    Rechazando...
                                </>
                            ) : (
                                'Sí, Rechazar Inspección'
                            )}
                        </button>
                    </div>
                </div>
            </Modal>

            {/* Notification Component */}
            <Notification
                visible={notification.visible}
                message={notification.message}
                type={notification.type}
                onClose={() => setNotification({ visible: false, message: '', type: 'info' })}
            />
        </>
    );
};

export default InspectionActions;